/**
 * EmployeeRefreshScheduler — Background pull of employee list + face embeddings.
 *
 * Why this exists (the gap it fixes):
 *   The push side (uploading enrolments from THIS terminal) is already covered
 *   by `syncWorker` — it drains the sync queue every 15s (or via Background Sync
 *   API in Chrome/Edge) and re-drains immediately on `window 'online'`. That's
 *   settled.
 *
 *   The pull side (learning about enrolments done on OTHER terminals) had no
 *   automatic refresh. `useEmployeesByAccessRoleQuery` only refetches when
 *   TanStack considers the query stale (5min) AND some component triggers it
 *   (mount / window focus / manual invalidate). If the user stays on a page
 *   that does not mount that query, this terminal never learns about new
 *   embeddings enrolled elsewhere.
 *
 *   This scheduler closes that gap by invalidating the employees query every
 *   minute, forcing a refetch that pulls the freshest DTOs. The composite
 *   repository seeds `db.faceEmbeddings` automatically on that fetch
 *   (`CompositeEmployeeRepository._seedFaceEmbeddings`), so both the employee
 *   list AND the offline-matcher cache stay warm.
 *
 * Responsibilities:
 * - Start/stop periodic invalidation timer (1-minute interval by default)
 * - Trigger an immediate refresh when the browser reports `online`
 * - Clean up resources on stop / profile switch / logout
 *
 * Non-goals:
 * - Does NOT push data — that is `syncWorker`'s job.
 * - Does NOT read Dexie directly — invalidation triggers the composite repo
 *   which already knows how to seed everything downstream.
 */

import type { QueryClient } from '@tanstack/react-query';
import { employeeKeys } from '@/modules/employees/application/employeeKeys';

/** Default refresh interval — 1 minute, per product requirement. */
const DEFAULT_INTERVAL_MS = 60 * 1000;

export class EmployeeRefreshScheduler {
  private static instance: EmployeeRefreshScheduler | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly refreshIntervalMs: number;
  private currentAccessRoleId: number | null = null;
  private queryClient: QueryClient | null = null;
  private onlineHandler: (() => void) | null = null;

  private constructor(intervalMs = DEFAULT_INTERVAL_MS) {
    this.refreshIntervalMs = intervalMs;
  }

  static getInstance(): EmployeeRefreshScheduler {
    if (!EmployeeRefreshScheduler.instance) {
      EmployeeRefreshScheduler.instance = new EmployeeRefreshScheduler();
    }
    return EmployeeRefreshScheduler.instance;
  }

  /**
   * Start periodic background refresh for the given access role.
   *
   * Behaviour:
   *   1. Registers a `window 'online'` listener that fires an immediate
   *      refresh — the moment connectivity returns, we pull without waiting
   *      for the next tick.
   *   2. Sets up a `setInterval` at `refreshIntervalMs` that invalidates the
   *      employees query. TanStack refetches, the composite repo hits the
   *      API, `_seedFaceEmbeddings` warms `db.faceEmbeddings`, and any
   *      mounted component re-renders with fresh data.
   *   3. Idempotent — calling `start()` while already running (e.g. on
   *      profile switch) safely stops the previous instance first.
   *
   * Call `stop()` on profile switch or logout to prevent stale syncs and
   * memory leaks.
   *
   * @param accessRoleId - The profile's access role to sync employees for.
   * @param queryClient  - React Query client used to invalidate the
   *                       `['employees', 'list', accessRoleId]` cache.
   */
  start(accessRoleId: number, queryClient: QueryClient): void {
    this.stop(); // Idempotent — clear any prior state

    this.currentAccessRoleId = accessRoleId;
    this.queryClient = queryClient;

    // Immediate-refresh-on-online: the moment the browser reports connectivity,
    // do not wait up to 60s for the next tick — pull now.
    this.onlineHandler = () => {
      void this.refresh();
    };
    window.addEventListener('online', this.onlineHandler);

    this.intervalId = setInterval(() => {
      void this.refresh();
    }, this.refreshIntervalMs);
  }

  /**
   * Stop the refresh scheduler and release all held resources.
   *
   * Clears the timer, removes the `online` listener, and nulls out
   * `currentAccessRoleId` and `queryClient`. Safe to call when no scheduler
   * is running (no-op in that case).
   *
   * Must be called on profile switch and on application logout.
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.currentAccessRoleId = null;
    this.queryClient = null;
  }

  /**
   * Force an immediate refresh (for manual triggering or testing).
   */
  async forceSync(): Promise<void> {
    await this.refresh();
  }

  /**
   * Trigger a refetch of the employees list for the current access role.
   *
   * Uses `refetchQueries` (not `invalidateQueries`) on purpose: this scheduler
   * runs regardless of which page is mounted. `invalidateQueries` only marks
   * the query stale — TanStack fires the HTTP request only if there is an
   * active observer (a mounted component using `useEmployeesByAccessRoleQuery`).
   * If the user is sitting on AdminMarcajesPage, that observer does not exist
   * and the network call never happens.
   *
   * `refetchQueries` forces the fetch even without observers, which is exactly
   * what a background pull needs. The composite repo's offline fallback still
   * kicks in when there is no network, so the call is safe offline.
   *
   * The composite repository (with its offline fallback, error handling and
   * face-embedding seed) runs through its normal flow. Any mounted component
   * subscribed to the query still re-renders with the fresh data.
   */
  private async refresh(): Promise<void> {
    if (!this.currentAccessRoleId || !this.queryClient) return;

    try {
      /*await this.queryClient.invalidateQueries({
        queryKey: employeeKeys.byAccessRole(this.currentAccessRoleId),
      });*/
      await this.queryClient.refetchQueries({
        queryKey: employeeKeys.byAccessRole(this.currentAccessRoleId),
      });
    } catch (error) {
      console.warn('[EmployeeRefreshScheduler] Refresh failed:', error);
    }
  }
}
